import { useState } from 'react';
import { Bar, BarChart, CartesianGrid, Cell, Line, LineChart, Pie, PieChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';

import { gnf } from '@/lib/invoices';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tooltip as HoverTip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { SummaryTile } from '@/components/vehicles/SummaryTile';

interface FuelBucket { liters: number; cost: number; count: number }

/** GET /fuel/stats (fuel-service), vue flotte */
export interface FuelStats {
  period: { from: string; to: string };
  totals: { liters: number; cost: number; transactions: number };
  /** Plein à plein sur les paires à odomètre croissant ; l100km null sans paire valide */
  consumption: { distKm: number | null; liters: number; l100km: number | null };
  bySource: Record<string, FuelBucket>;
  monthly: ({ month: string } & FuelBucket)[];
  vehicles: FuelVehicleStats[];
  topConsumers: FuelVehicleStats[];
}

interface FuelVehicleStats {
  vehicleId: string;
  plate: string;
  totalLiters: number;
  totalCost: number;
  txCount: number;
  distKm: number | null;
  consumedLiters: number;
  l100km: number | null;
}

// Palette catégorielle validée (créneaux 1-3, fond blanc) : ordre fixe, la couleur suit la source
const SERIES_1 = '#2a78d6';
const SOURCES = [
  { key: 'manual', label: 'Manuel', color: '#2a78d6' },
  { key: 'driver_app', label: 'App chauffeur', color: '#eb6834' },
  { key: 'import', label: 'Import', color: '#1baf7a' },
] as const;

const AXIS_TICK = { fill: 'hsl(var(--muted-foreground))', fontSize: 12 };
const GRID = 'hsl(var(--border))';

const fmtLiters = (l: number) => `${l.toLocaleString('fr-FR', { maximumFractionDigits: 1 })} L`;
const fmtCompact = (n: number) => n.toLocaleString('fr-FR', { notation: 'compact', maximumFractionDigits: 1 });
const fmtMonth = (m: string) =>
  new Date(`${m}-01T00:00:00Z`).toLocaleDateString('fr-FR', { month: 'short', year: '2-digit', timeZone: 'UTC' });

export function FuelStatsPanel({ stats }: { stats: FuelStats }) {
  const { totals, consumption } = stats;
  return (
    <div className="space-y-6">
      <div className="grid gap-4 grid-cols-2 lg:grid-cols-4">
        <SummaryTile label="Litres" value={fmtLiters(totals.liters)} />
        <SummaryTile label="Coût" value={gnf(totals.cost)} />
        <SummaryTile label="Pleins" value={totals.transactions.toLocaleString('fr-FR')} />
        <FleetConsumptionTile consumption={consumption} />
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <TopConsumersChart vehicles={stats.topConsumers} />
        <SourceDonut bySource={stats.bySource} total={totals.liters} />
      </div>

      <MonthlyChart monthly={stats.monthly} />
      <VehicleTable vehicles={stats.vehicles} />
    </div>
  );
}

function FleetConsumptionTile({ consumption }: { consumption: FuelStats['consumption'] }) {
  if (consumption.l100km != null) {
    return (
      <SummaryTile
        label="Conso moyenne flotte"
        value={`${consumption.l100km.toLocaleString('fr-FR', { maximumFractionDigits: 1 })} L/100 km`}
        sub={`${fmtLiters(consumption.liters)} sur ${(consumption.distKm ?? 0).toLocaleString('fr-FR')} km mesurés`}
      />
    );
  }
  return (
    <TooltipProvider delayDuration={200}>
      <HoverTip>
        <TooltipTrigger asChild>
          <div tabIndex={0} className="cursor-help">
            <SummaryTile label="Conso moyenne flotte" value="N/A" sub="données insuffisantes" />
          </div>
        </TooltipTrigger>
        <TooltipContent className="max-w-xs">
          La conso se calcule plein à plein : il faut au moins deux pleins consécutifs d’un même véhicule avec un
          odomètre croissant sur la période.
        </TooltipContent>
      </HoverTip>
    </TooltipProvider>
  );
}

function TopConsumersChart({ vehicles }: { vehicles: FuelVehicleStats[] }) {
  return (
    <Card>
      <CardHeader><CardTitle className="text-base">Top 5 véhicules consommateurs</CardTitle></CardHeader>
      <CardContent>
        <ResponsiveContainer width="100%" height={Math.max(120, vehicles.length * 40 + 40)}>
          <BarChart data={vehicles} layout="vertical" margin={{ left: 8, right: 16 }}>
            <CartesianGrid horizontal={false} stroke={GRID} />
            <XAxis type="number" tick={AXIS_TICK} tickFormatter={(v: number) => `${fmtCompact(v)} L`} axisLine={false} tickLine={false} />
            <YAxis type="category" dataKey="plate" width={96} tick={AXIS_TICK} axisLine={false} tickLine={false} />
            <Tooltip
              cursor={{ fill: 'hsl(var(--muted))' }}
              formatter={(v: number, _n, item) => [`${fmtLiters(v)} · ${gnf((item.payload as FuelVehicleStats).totalCost)}`, 'Litres']}
            />
            <Bar dataKey="totalLiters" fill={SERIES_1} radius={[0, 4, 4, 0]} barSize={18} />
          </BarChart>
        </ResponsiveContainer>
      </CardContent>
    </Card>
  );
}

function SourceDonut({ bySource, total }: { bySource: FuelStats['bySource']; total: number }) {
  const data = SOURCES.map((s) => ({ ...s, ...(bySource[s.key] ?? { liters: 0, cost: 0, count: 0 }) }));
  return (
    <Card>
      <CardHeader><CardTitle className="text-base">Répartition par source</CardTitle></CardHeader>
      <CardContent className="flex flex-col sm:flex-row items-center gap-6">
        <div className="h-44 w-44 shrink-0">
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie data={data} dataKey="liters" nameKey="label" innerRadius="60%" outerRadius="100%" paddingAngle={2} stroke="#fff" strokeWidth={2}>
                {data.map((s) => <Cell key={s.key} fill={s.color} />)}
              </Pie>
              <Tooltip formatter={(v: number, name: string) => [fmtLiters(v), name]} />
            </PieChart>
          </ResponsiveContainer>
        </div>
        {/* Légende chiffrée : l'identité ne repose pas sur la seule couleur */}
        <ul className="space-y-3 text-sm w-full min-w-0">
          {data.map((s) => (
            <li key={s.key} className="flex items-start gap-2">
              <span className="h-3 w-3 mt-1 rounded-sm shrink-0" style={{ background: s.color }} aria-hidden />
              <div className="flex-1 min-w-0">
                <div className="flex items-baseline justify-between gap-2">
                  <span className="whitespace-nowrap">{s.label}</span>
                  <span className="tabular-nums whitespace-nowrap font-medium">{fmtLiters(s.liters)}</span>
                </div>
                <p className="text-xs text-muted-foreground tabular-nums">
                  {s.count} plein{s.count > 1 ? 's' : ''} · {total > 0 ? Math.round((s.liters / total) * 100) : 0} %
                </p>
              </div>
            </li>
          ))}
        </ul>
      </CardContent>
    </Card>
  );
}

function MonthlyChart({ monthly }: { monthly: FuelStats['monthly'] }) {
  // Litres et coût n'ont pas la même échelle : une mesure à la fois, jamais deux axes
  const [measure, setMeasure] = useState<'liters' | 'cost'>('liters');
  const fmt = measure === 'liters' ? fmtLiters : (v: number) => gnf(v);
  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0">
        <CardTitle className="text-base">Évolution mensuelle</CardTitle>
        <div className="flex gap-1" role="group" aria-label="Mesure">
          {(['liters', 'cost'] as const).map((m) => (
            <Button key={m} size="sm" variant={measure === m ? 'default' : 'outline'} aria-pressed={measure === m} onClick={() => setMeasure(m)}>
              {m === 'liters' ? 'Litres' : 'Coût'}
            </Button>
          ))}
        </div>
      </CardHeader>
      <CardContent>
        <ResponsiveContainer width="100%" height={240}>
          <LineChart data={monthly} margin={{ left: 8, right: 16, top: 8 }}>
            <CartesianGrid vertical={false} stroke={GRID} />
            <XAxis dataKey="month" tickFormatter={fmtMonth} tick={AXIS_TICK} axisLine={false} tickLine={false} />
            <YAxis tick={AXIS_TICK} tickFormatter={(v: number) => fmtCompact(v)} axisLine={false} tickLine={false} width={48} />
            <Tooltip
              labelFormatter={(m: string) => fmtMonth(m)}
              formatter={(v: number, _n, item) => [`${fmt(v)} · ${(item.payload as FuelBucket).count} plein(s)`, measure === 'liters' ? 'Litres' : 'Coût']}
            />
            <Line type="linear" dataKey={measure} stroke={SERIES_1} strokeWidth={2} dot={{ r: 4, fill: SERIES_1 }} activeDot={{ r: 5 }} />
          </LineChart>
        </ResponsiveContainer>
      </CardContent>
    </Card>
  );
}

function VehicleTable({ vehicles }: { vehicles: FuelVehicleStats[] }) {
  return (
    <div className="bg-card rounded-lg border">
      <div className="p-4 border-b"><h2 className="font-semibold">Détail par véhicule</h2></div>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Véhicule</TableHead>
            <TableHead className="text-right">Pleins</TableHead>
            <TableHead className="text-right">Litres</TableHead>
            <TableHead className="text-right">Coût</TableHead>
            <TableHead className="text-right">Distance mesurée</TableHead>
            <TableHead className="text-right">L/100 km</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {vehicles.map((v) => (
            <TableRow key={v.vehicleId}>
              <TableCell>{v.plate}</TableCell>
              <TableCell className="text-right tabular-nums">{v.txCount}</TableCell>
              <TableCell className="text-right tabular-nums">{fmtLiters(v.totalLiters)}</TableCell>
              <TableCell className="text-right tabular-nums">{gnf(v.totalCost)}</TableCell>
              <TableCell className="text-right tabular-nums">{v.distKm != null ? `${v.distKm.toLocaleString('fr-FR')} km` : '—'}</TableCell>
              <TableCell className="text-right tabular-nums">
                {v.l100km != null ? v.l100km.toLocaleString('fr-FR', { maximumFractionDigits: 1 }) : '—'}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}
